/*
  The teacher's last grade and subject, remembered in this browser (two-paths review, items A3 + B5,
  15 Oct 2026).

  One localStorage key, ttv-last-grade-subject = {"grade": 4, "subject": "mathematics", "at": <ms>}.
  Every read and write is wrapped in try/catch: a private window, blocked storage or a preview with
  no storage simply means nothing is remembered, and every page renders exactly as it does without
  this file.

  Written when the teacher shows which grade they teach:
    - a grade or grade+subject collection page (/collections/grade-4, /collections/grade-4-language)
    - a unit's product page (/products/grade-4-...: the grade only, the subject is kept)
    - the generator page picking a grade (snippets/generator-script-account.liquid, chooseGrade)
    - the home hero's own "change" row and grade chips (sections/search-hero.liquid)
  Read by:
    - the home hero: "For Grade 4 · Mathematics" above real one-tap requests ("Popular for tomorrow"),
      and the "Get a finished unit" box's grade chips, which lead with the remembered grade
    - the Grades menu (desktop dropdown and phone drawer): the remembered grade moves to the top

  The generator page itself still starts every plain visit at its first step (G1 item 3, Beau 28 Sep):
  this file never presses anything there.

  Pure helpers are exported under Node for tests/two-paths-remember.test.js.
*/
(function (global) {
  'use strict';

  var KEY = 'ttv-last-grade-subject';

  // ---- TTVSubjects (21 Oct 2026) ------------------------------------------------------------
  // The subjects are data: Theme settings > Subjects, rendered once per page by
  // snippets/ttv-subjects.liquid as <script id="ttv-subjects">, in setting order. Nothing in this
  // file names a subject, so one added in the setting needs no script change.
  //   list            [{ slug, label, short, words, colour, live: [grades] }]
  //   bySlug(slug)    entry or null
  //   liveAt(grade)   entries with products at that grade
  //   words()         { word: slug } for every word of every subject
  //   label(slug), short(slug)
  //   load(list) and parse(settingText) are for tests and pages without the JSON.
  var TTVSubjects = { list: [] };
  function lower(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  function subjectsLoad(list) {
    TTVSubjects.list = (Array.isArray(list) ? list : []).filter(function (s) { return s && lower(s.slug); }).map(function (s) {
      var slug = lower(s.slug);
      var label = String(s.label || slug);
      return {
        slug: slug, label: label, short: String(s.short || label),
        words: (Array.isArray(s.words) ? s.words : []).map(lower).filter(Boolean),
        colour: String(s.colour || ''),
        live: (Array.isArray(s.live) ? s.live : []).map(function (g) { return parseInt(g, 10); }).filter(function (g) { return g >= 1 && g <= 8; })
      };
    });
    return TTVSubjects.list;
  }
  // "slug | label | short | words | colour", one per line: the setting's own format.
  function subjectsParse(text) {
    return String(text || '').split(/\r?\n/).map(function (line) {
      var c = line.split('|').map(function (x) { return x.trim(); });
      if (!c[0]) return null;
      return { slug: c[0], label: c[1], short: c[2], words: String(c[3] || '').split(','), colour: c[4], live: [] };
    }).filter(Boolean);
  }
  TTVSubjects.load = subjectsLoad;
  TTVSubjects.parse = subjectsParse;
  TTVSubjects.bySlug = function (slug) {
    slug = lower(slug);
    for (var i = 0; i < TTVSubjects.list.length; i++) if (TTVSubjects.list[i].slug === slug) return TTVSubjects.list[i];
    return null;
  };
  TTVSubjects.liveAt = function (grade) {
    var g = parseInt(grade, 10);
    return TTVSubjects.list.filter(function (s) { return s.live.indexOf(g) >= 0; });
  };
  TTVSubjects.words = function () {
    var out = {};
    TTVSubjects.list.forEach(function (s) { s.words.forEach(function (w) { if (!(w in out)) out[w] = s.slug; }); });
    return out;
  };
  TTVSubjects.label = function (slug) { var s = TTVSubjects.bySlug(slug); return s ? s.label : ''; };
  TTVSubjects.short = function (slug) { var s = TTVSubjects.bySlug(slug); return s ? s.short : ''; };
  try {
    var subjectsNode = global.document && global.document.getElementById('ttv-subjects');
    if (subjectsNode) subjectsLoad(JSON.parse(subjectsNode.textContent || '[]'));
  } catch (e) { /* no subjects: nothing is remembered as a subject */ }

  // Real requests a teacher can run in one tap, per subject: Theme settings > Subjects >
  // "Popular requests" (ttv_popular_<slug>, one "grade N ..." request per line), handed over by the
  // home hero (usePopular). The defaults were checked against the generator's lesson file
  // (15 Oct 2026); tests/two-paths-quick-wins.test.js re-checks them against tests/fixtures/lessons.json.
  var POPULAR = {};
  function usePopular(map) {
    Object.keys(map || {}).forEach(function (slug) {
      var byGrade = {};
      (Array.isArray(map[slug]) ? map[slug] : []).forEach(function (line) {
        var m = /^grade ([1-8]) (.+)$/.exec(lower(line).replace(/\s+/g, ' '));
        if (m) (byGrade[m[1]] = byGrade[m[1]] || []).push(m[2]);
      });
      POPULAR[lower(slug).replace(/_/g, '-')] = byGrade;
    });
    return POPULAR;
  }
  // Nothing remembered yet: three requests that name their own grade.
  var POPULAR_ANY = ['grade 4 fractions exit card', 'grade 3 multiplication homework', 'grade 5 area worksheet'];

  function cleanGrade(g) {
    var n = parseInt(g, 10);
    return n >= 1 && n <= 8 ? n : null;
  }
  // A slug, a label or one of the subject's words ("maths") -> its slug; anything else -> null.
  function cleanSubject(s) {
    s = lower(s);
    if (!s) return null;
    if (TTVSubjects.bySlug(s)) return s;
    var hit = null;
    TTVSubjects.list.some(function (x) { if (lower(x.label) === s) { hit = x.slug; return true; } return false; });
    return hit || TTVSubjects.words()[s] || null;
  }
  function capital(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }

  // The one-tap requests for a grade and subject: [{ label, q }]. With no grade, the any-grade set;
  // with no subject, the setting's first.
  function popular(grade, subject) {
    var g = cleanGrade(grade);
    var s = cleanSubject(subject) || (TTVSubjects.list[0] && TTVSubjects.list[0].slug);
    if (!g) return POPULAR_ANY.map(function (q) { return { label: capital(q), q: q }; });
    return ((POPULAR[s] || {})[g] || []).map(function (t) { return { label: capital(t), q: 'grade ' + g + ' ' + t }; });
  }

  // Grade and subject read from a store path, or null:
  //   /collections/grade-4             -> { grade: 4, subject: null }
  //   /collections/grade-4-language    -> { grade: 4, subject: 'language' }
  //   /products/grade-4-number-sense-full-unit -> { grade: 4, subject: null }
  function fromPath(path) {
    var p = String(path || '').toLowerCase();
    var m = /\/collections\/grade-([1-8])(?:-([a-z-]+))?\/?$/.exec(p);
    if (m) return { grade: parseInt(m[1], 10), subject: cleanSubject(m[2]) };
    m = /\/products\/grade-([1-8])-/.exec(p);
    if (m) return { grade: parseInt(m[1], 10), subject: null };
    return null;
  }

  // The unit list for a grade and subject (the "Get a finished unit" chips).
  function unitsUrl(grade, subject) {
    var g = cleanGrade(grade);
    if (!g) return '/collections/all';
    var s = cleanSubject(subject);
    return '/collections/grade-' + g + (s ? '-' + s : '');
  }

  function storage() {
    try { return global.localStorage || null; } catch (e) { return null; }
  }
  function get() {
    var st = storage();
    if (!st) return null;
    try {
      var v = JSON.parse(st.getItem(KEY) || 'null');
      var g = v && cleanGrade(v.grade);
      if (!g) return null;
      return { grade: g, subject: cleanSubject(v.subject) };
    } catch (e) { return null; }
  }
  // A missing subject keeps the one already remembered (a product page knows only its grade).
  function set(grade, subject) {
    var g = cleanGrade(grade);
    if (!g) return null;
    var s = cleanSubject(subject);
    var old = get();
    if (!s && old) s = old.subject;
    var v = { grade: g, subject: s, at: Date.now() };
    var st = storage();
    if (st) { try { st.setItem(KEY, JSON.stringify(v)); } catch (e) { /* nothing remembered */ } }
    return { grade: g, subject: s };
  }

  var api = {
    KEY: KEY, POPULAR: POPULAR, POPULAR_ANY: POPULAR_ANY, TTVSubjects: TTVSubjects, usePopular: usePopular,
    cleanGrade: cleanGrade, cleanSubject: cleanSubject, popular: popular, fromPath: fromPath,
    unitsUrl: unitsUrl, get: get, set: set
  };
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (global.TTVRemember) return;
  global.TTVRemember = api;
  global.TTVSubjects = TTVSubjects;

  // ---- Browser only ----------------------------------------------------------------------

  // The Grades menu leads with the remembered grade: its link moves to the top of each grade list
  // (desktop dropdown #desktop-menu-0-grades, phone drawer .mobile-menu__nav--grades), marked
  // "Your grade". Nothing else in the menu changes.
  function leadMenus(r) {
    if (!r) return;
    var lists = document.querySelectorAll('#desktop-menu-0-grades, .mobile-menu__nav--grades');
    Array.prototype.forEach.call(lists, function (ul) {
      if (ul.getAttribute('data-ttv-led')) return;
      var links = ul.querySelectorAll('a[href]');
      for (var i = 0; i < links.length; i++) {
        var path = links[i].getAttribute('href').split('?')[0];
        var hit = fromPath(path);
        if (!hit || hit.grade !== r.grade || hit.subject) continue;
        var li = links[i].closest('li');
        if (!li || li.parentNode !== ul) continue;
        ul.setAttribute('data-ttv-led', '1');
        var first = ul.querySelector(':scope > li');
        if (first && first !== li) ul.insertBefore(li, first);
        if (!links[i].querySelector('.ttv-your-grade')) {
          var tag = document.createElement('span');
          tag.className = 'ttv-your-grade';
          tag.textContent = 'your grade';
          links[i].appendChild(tag);
        }
        return;
      }
    });
  }

  function boot() {
    var here = fromPath(global.location && global.location.pathname);
    if (here) set(here.grade, here.subject);
    leadMenus(get());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
